import { useState } from "react";
import {
  calculateUserPoints,
  calculateDirectorPoints,
  calculateDirectorReservedPoints,
  getDirectorStatus,
} from "../utils/calculations";
import logoMark from "../assets/logo-mark.png";
import logoMarkMax from "../assets/logo-mark-max.png";

// ── Helper: format an expiry date as "14 June 2026" ──
// Accepts ISO ("2026-06-14"), or any parseable string. Falls back to raw value.
function formatExpiryDate(value) {
  // ── Passthrough display ──
  // Whatever the admin typed in the Excel cell goes straight to the screen.
  // No timezone math, no date parsing — just show what was uploaded.
  if (!value) return "—";
  return String(value);
}

// ── Helper: converts days left into a readable pill label + colour class ──
function getDaysLabel(daysLeft) {
  if (daysLeft < 0)  return { text: "Expired",          cls: "ggd-days--expired" };
  if (daysLeft === 0) return { text: "Expires today",   cls: "ggd-days--critical" };
  if (daysLeft === 1) return { text: "1 day left",      cls: "ggd-days--critical" };
  if (daysLeft <= 3)  return { text: `${daysLeft} days left`, cls: "ggd-days--critical" };
  if (daysLeft <= 7)  return { text: `${daysLeft} days left`, cls: "ggd-days--warning" };
  if (daysLeft <= 30) return { text: `${daysLeft} days left`, cls: "ggd-days--ok" };
  return { text: `${daysLeft} days left`,               cls: "ggd-days--safe" };
}

// ── Snowflake component for Tier 2 celebration ──
function Snowfall() {
  const flakes = [
    { left: "5%",  size: 10, duration: 6,   delay: 0   },
    { left: "12%", size: 14, duration: 8,   delay: 1   },
    { left: "20%", size: 8,  duration: 5,   delay: 2.5 },
    { left: "28%", size: 12, duration: 7,   delay: 0.5 },
    { left: "36%", size: 16, duration: 9,   delay: 1.5 },
    { left: "44%", size: 9,  duration: 6.5, delay: 3   },
    { left: "52%", size: 13, duration: 7.5, delay: 0.8 },
    { left: "60%", size: 11, duration: 8.5, delay: 2   },
    { left: "68%", size: 15, duration: 6,   delay: 1.2 },
    { left: "76%", size: 8,  duration: 9,   delay: 3.5 },
    { left: "84%", size: 12, duration: 7,   delay: 0.3 },
    { left: "91%", size: 10, duration: 5.5, delay: 2.8 },
    { left: "97%", size: 14, duration: 8,   delay: 1.8 },
  ];

  return (
    <div className="ggd-snowfall" aria-hidden="true">
      {flakes.map((f, i) => (
        <div
          key={i}
          className="ggd-snowflake"
          style={{
            left: f.left,
            fontSize: f.size,
            animationDuration: `${f.duration}s`,
            animationDelay: `${f.delay}s`,
          }}
        >
          ❄
        </div>
      ))}
    </div>
  );
}

export default function Dashboard({ userData, directors = [], onLogout }) {
  const [showCpPopup, setShowCpPopup] = useState(false);
  const [showComingSoon, setShowComingSoon] = useState(false);
  const [comingSoonFeature, setComingSoonFeature] = useState("");
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const points = calculateUserPoints(userData);
  const directorPoints         = calculateDirectorPoints(directors);         // qualified → confirmed
  const directorReservedPoints = calculateDirectorReservedPoints(directors); // waiting → reserved

  // Actual = user's earned points + graduated (qualified) director points
  // Projected = Actual + still-reserved director points
  const actualPoints    = points.confirmedPoints + directorPoints;
  const projectedPoints = actualPoints + directorReservedPoints;

  // ── Tier detection based on ACTUAL points ──
  // Qualified director points count. Reserved points don't.
  const isTier2 = actualPoints >= 60;
  const isTier1 = !isTier2 && actualPoints >= 30;

  const reportDate = userData.reportDate
    ? new Date(userData.reportDate).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : "—";

  // ── Qualification countdown — weeks left until Sep 26, 2026 ──
  const qualEndDate = new Date("2026-09-26");
  const msLeft = qualEndDate - new Date();
  const daysLeft = Math.max(0, Math.ceil(msLeft / (1000 * 60 * 60 * 24)));
  const weeksLeft = Math.max(0, Math.ceil(daysLeft / 7));
  const qualEnded = msLeft <= 0;

  return (
    <div className={`ggd-shell ${sidebarCollapsed ? "sidebar-collapsed" : ""} ${isTier2 ? "tier-2" : isTier1 ? "tier-1" : ""}`}>

      {/* ── Tier 2 snowfall ── */}
      {isTier2 && <Snowfall />}

      {/* ── Sidebar ── */}
      <aside className="ggd-sidebar">
        <div className="ggd-sidebar-top">
          <div className="ggd-logo">
            {/* Collapsed: show small logo mark icon */}
            {/* Expanded: show full logo text */}
            {sidebarCollapsed ? (
              <img
                src={logoMark}
                alt="Go Getter Discovery"
                className="ggd-logo-img-mark"
              />
            ) : (
              <img
                src={logoMarkMax}
                alt="USANA Go Getter Discovery"
                className="ggd-logo-img-full"
              />
            )}
          </div>
          <button
            className="ggd-collapse-btn"
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            aria-label="Toggle sidebar"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d={sidebarCollapsed ? "M6 3l5 5-5 5" : "M10 3L5 8l5 5"}
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>

        <nav className="ggd-nav">
          <button className="ggd-nav-item active">
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <rect x="1" y="1" width="6" height="6" rx="2" stroke="currentColor" strokeWidth="1.6" />
              <rect x="11" y="1" width="6" height="6" rx="2" stroke="currentColor" strokeWidth="1.6" />
              <rect x="1" y="11" width="6" height="6" rx="2" stroke="currentColor" strokeWidth="1.6" />
              <rect x="11" y="11" width="6" height="6" rx="2" stroke="currentColor" strokeWidth="1.6" />
            </svg>
            {!sidebarCollapsed && <span>Dashboard</span>}
          </button>
          <button className="ggd-nav-item" onClick={() => { setComingSoonFeature("Directors"); setShowComingSoon(true); }}>
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <circle cx="9" cy="6" r="4" stroke="currentColor" strokeWidth="1.6" />
              <path d="M2 17c0-3.866 3.134-7 7-7s7 3.134 7 7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
            </svg>
            {!sidebarCollapsed && <span>Directors</span>}
          </button>
          <button className="ggd-nav-item" onClick={() => { setComingSoonFeature("Points"); setShowComingSoon(true); }}>
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path d="M9 1l2.245 5.998H17l-4.878 3.502 1.754 5.998L9 13.002l-4.876 3.498 1.754-5.998L1 7h5.755z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
            </svg>
            {!sidebarCollapsed && <span>Points</span>}
          </button>
          <button className="ggd-nav-item" onClick={() => { setComingSoonFeature("Reports"); setShowComingSoon(true); }}>
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path d="M2 10h14M2 6h14M2 14h8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
            </svg>
            {!sidebarCollapsed && <span>Reports</span>}
          </button>
        </nav>

        <div className="ggd-sidebar-footer">
          {onLogout && (
            <button className="ggd-nav-item ggd-logout-btn" onClick={onLogout}>
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path d="M12 12.5l3.5-3.5L12 5.5M15.5 9H7M7 2H3a1 1 0 00-1 1v12a1 1 0 001 1h4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              {!sidebarCollapsed && <span>Logout</span>}
            </button>
          )}
        </div>
      </aside>

      {/* ── Main Content ── */}
      <div className="ggd-main">

        <header className="ggd-header">
          <div className="ggd-header-left">
            <div className="ggd-title-row">
              <h1 className="ggd-page-title">Go Getter Discovery</h1>
              <span className="ggd-part-pill">Round 2</span>
            </div>
            <p className="ggd-report-date">Result as of : {reportDate}</p>
          </div>
          <div className="ggd-header-right">
            {qualEnded ? (
              <div className="ggd-countdown ggd-countdown--ended">
                <div className="ggd-countdown-icon">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.5"/>
                    <path d="M8 4.5V8l2.5 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <div className="ggd-countdown-text">
                  <span className="ggd-countdown-num">Ended</span>
                  <span className="ggd-countdown-label">Qualification closed</span>
                </div>
              </div>
            ) : (
              <div className="ggd-countdown">
                <div className="ggd-countdown-pulse" />
                <div className="ggd-countdown-icon">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.5"/>
                    <path d="M8 4.5V8l2.5 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <div className="ggd-countdown-text">
                  <span className="ggd-countdown-num">
                    {weeksLeft}<span className="ggd-countdown-unit">{weeksLeft === 1 ? " week" : " weeks"}</span>
                  </span>
                  <span className="ggd-countdown-label">left to qualify</span>
                </div>
              </div>
            )}

            <div className="ggd-avatar">
              {userData.name?.charAt(0) ?? "U"}
            </div>
          </div>
        </header>

        <div className="ggd-content">

          {/* ── Tier 2 celebration banner ── */}
          {isTier2 && (
            <div className="ggd-tier2-banner">
              <div className="ggd-tier2-banner-glow" />
              <div className="ggd-tier2-banner-content">
                <span className="ggd-tier2-icon">👑</span>
                <div>
                  <p className="ggd-tier2-banner-title">Tier 2 Qualified — Free Trip for 2 Pax!</p>
                  <p className="ggd-tier2-banner-sub">Congratulations! You've earned the 4D3N Hokkaido Japan trip for 2 pax. Keep it up!</p>
                </div>
              </div>
            </div>
          )}

          {/* ── Tier 1 congratulation banner ── */}
          {isTier1 && (
            <div className="ggd-tier1-banner">
              <div className="ggd-tier1-banner-content">
                <span className="ggd-tier1-icon">🎉</span>
                <div>
                  <p className="ggd-tier1-banner-title">Tier 1 Qualified — Free Trip for 1 Pax!</p>
                  <p className="ggd-tier1-banner-sub">Amazing work! You've qualified for the 4D3N Hokkaido Japan trip. Reach 60 pts to bring a guest!</p>
                </div>
              </div>
            </div>
          )}

          {/* ── Profile + Projected ── */}
          <div className="ggd-top-row">
            <section className="ggd-card ggd-profile-card">
              <div className="ggd-profile-badge">
                {userData.name?.charAt(0) ?? "U"}
              </div>
              <div className="ggd-profile-info">
                <p className="ggd-eyebrow">Brand Partner</p>
                <h2 className="ggd-profile-name">{userData.name}</h2>
                <p className="ggd-profile-sub">ID: {userData.usanaId}</p>
              </div>
              <div className="ggd-rank-chip">{userData.rank}</div>
            </section>

            {/* ── Points card — changes based on tier ── */}
            {isTier2 ? (
              <section className="ggd-card ggd-projected-card ggd-projected-card--tier2">
                <div className="ggd-tier-ribbon ggd-tier-ribbon--2">TIER 2 👑</div>
                <p className="ggd-eyebrow ggd-eyebrow-light">Actual Points</p>
                <div className="ggd-projected-num ggd-projected-num--celebrate">{actualPoints.toLocaleString()}</div>
                <p className="ggd-tier-qualify-msg">Free Trip for 2 Pax · Hokkaido Japan 🗾</p>
                <div className="ggd-projected-breakdown ggd-projected-breakdown--tier">
                  <div className="ggd-breakdown-item">
                    <span>Projected Points</span>
                    <strong>{projectedPoints.toLocaleString()}</strong>
                  </div>
                  <div className="ggd-breakdown-divider" />
                  <div className="ggd-breakdown-item">
                    <span>Director Reserved</span>
                    <strong>{directorReservedPoints.toLocaleString()}</strong>
                  </div>
                </div>
              </section>
            ) : isTier1 ? (
              <section className="ggd-card ggd-projected-card ggd-projected-card--tier1">
                <div className="ggd-tier-ribbon ggd-tier-ribbon--1">TIER 1 🥇</div>
                <p className="ggd-eyebrow ggd-eyebrow-light">Actual Points</p>
                <div className="ggd-projected-num ggd-projected-num--celebrate">{actualPoints.toLocaleString()}</div>
                <p className="ggd-tier-qualify-msg">Free Trip for 1 Pax · Hokkaido Japan 🗾</p>
                <div className="ggd-projected-breakdown ggd-projected-breakdown--tier">
                  <div className="ggd-breakdown-item">
                    <span>Projected Points</span>
                    <strong>{projectedPoints.toLocaleString()}</strong>
                  </div>
                  <div className="ggd-breakdown-divider" />
                  <div className="ggd-breakdown-item">
                    <span>Director Reserved</span>
                    <strong>{directorReservedPoints.toLocaleString()}</strong>
                  </div>
                </div>
              </section>
            ) : (
              <section className="ggd-card ggd-projected-card">
                <p className="ggd-eyebrow ggd-eyebrow-light">Actual Points</p>
                <div className="ggd-projected-num">{actualPoints.toLocaleString()}</div>
                <div className="ggd-projected-breakdown">
                  <div className="ggd-breakdown-item">
                    <span>Projected Points</span>
                    <strong>{projectedPoints.toLocaleString()}</strong>
                  </div>
                  <div className="ggd-breakdown-divider" />
                  <div className="ggd-breakdown-item">
                    <span>Director Reserved</span>
                    <strong>{directorReservedPoints.toLocaleString()}</strong>
                  </div>
                </div>
              </section>
            )}

          </div>

          {/* ── Metric cards ── */}
          <div className="ggd-metric-row">
            <div className="ggd-card ggd-metric-card">
              <div className="ggd-metric-icon ggd-metric-icon--blue">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M8 1l1.796 4.798H14l-3.9 2.8 1.403 4.799L8 10.597l-3.503 2.8 1.403-4.799L2 5.798h4.204z" fill="currentColor" />
                </svg>
              </div>
              <p className="ggd-metric-label">Pacesetter Points</p>
              <h3 className="ggd-metric-value">{points.pacesetterPoints.toLocaleString()}</h3>
              <p className="ggd-metric-sub">{points.pacesetterCreated} created</p>
            </div>

            <button className="ggd-card ggd-metric-card ggd-cp-card" onClick={() => setShowCpPopup(true)}>
              <div className="ggd-metric-icon ggd-metric-icon--violet">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.8" />
                  <path d="M8 5v3l2 2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                </svg>
              </div>
              <p className="ggd-metric-label">CP Points</p>
              <h3 className="ggd-metric-value">{points.cpGrowthPoints.toLocaleString()}</h3>
              <p className="ggd-metric-sub ggd-metric-link">View details →</p>
            </button>

            <div className="ggd-card ggd-metric-card">
              <div className="ggd-metric-icon ggd-metric-icon--emerald">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M3 14V8M8 14V2M13 14V6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                </svg>
              </div>
              <p className="ggd-metric-label">Director Points</p>
              <h3 className="ggd-metric-value">{(directorPoints + directorReservedPoints).toLocaleString()}</h3>
              <p className="ggd-metric-sub">
                {directorPoints > 0
                  ? "Qualified ✓"
                  : directorReservedPoints > 0
                    ? "Awaiting qualification"
                    : "None qualifying"}
              </p>
            </div>
          </div>

          {/* ── Directors section ── */}
          <section className="ggd-card ggd-directors-section">
            <div className="ggd-section-header">
              <div>
                <h2 className="ggd-section-title">My Directors</h2>
                <p className="ggd-section-sub">{directors.length} record{directors.length !== 1 ? "s" : ""}</p>
              </div>
            </div>

            {directors.length === 0 ? (
              <div className="ggd-empty-state">
                <div className="ggd-empty-icon">
                  <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                    <circle cx="16" cy="12" r="6" stroke="currentColor" strokeWidth="1.8" />
                    <path d="M6 28c0-5.523 4.477-10 10-10s10 4.477 10 10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                  </svg>
                </div>
                <p className="ggd-empty-title">No directors yet</p>
                <p className="ggd-empty-body">Directors you create will appear here.</p>
              </div>
            ) : (
              <div className="ggd-director-list">
                {directors.map((director) => {
                  const status = getDirectorStatus(director);

                  // ── Get day label for 50CP expiry date ──
                  const fiftyCpLabel  = getDaysLabel(status.fiftyCpDaysLeft);

                  return (
                    <article
                      key={director.id}
                      className={`ggd-director-card ggd-director-card--${status.warningLevel || "default"}`}
                    >
                      <div className="ggd-director-top">
                        <div className="ggd-director-avatar">
                          {director.directorName?.charAt(0) ?? "D"}
                        </div>
                        <div className="ggd-director-info">
                          <p className="ggd-director-id">ID: {director.directorUsanaId}</p>
                          <h3 className="ggd-director-name">{director.directorName}</h3>
                        </div>
                        <div className="ggd-director-badges">
                          <span className="ggd-badge ggd-badge--rank ggd-badge--rank-lg">
                            {director.newRank}
                          </span>
                          {director.qualificationStatus === "qualified" && (
                            <span className="ggd-badge ggd-badge--qualified">
                              <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
                                <circle cx="5.5" cy="5.5" r="5" fill="currentColor" opacity="0.15"/>
                                <path d="M3 5.5l2 2 3.5-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                              Qualified
                            </span>
                          )}
                        </div>
                      </div>

                      {/* ── Expiry row — now shows date + days remaining pill ── */}
                      <div className="ggd-expiry-row">
                        <div className="ggd-expiry-item">
                          <span className="ggd-expiry-label">Commision qualified : Next Subscribe & Save</span>
                          <strong className="ggd-expiry-date ggd-expiry-date--large">
                            {formatExpiryDate(director.comQuaExpiryDate)}
                          </strong>
                        </div>
                        <div className="ggd-expiry-item">
                          <span className="ggd-expiry-label">Earn Min 50CP Before :</span>
                          <strong className="ggd-expiry-date">
                            {formatExpiryDate(director.fiftyCpExpiryDate)}
                          </strong>
                          <span className={`ggd-days-pill ${fiftyCpLabel.cls}`}>
                            {fiftyCpLabel.text}
                          </span>
                        </div>
                      </div>

                      {status.warningLevel === "warning" && (
                        <div className="ggd-alert ggd-alert--warning">
                          Update needed soon — refresh this director's qualification before the expiry date.
                        </div>
                      )}
                      {status.warningLevel === "critical" && (
                        <div className="ggd-alert ggd-alert--warning">
                          Update needed — please refresh this director's qualification.
                        </div>
                      )}
                    </article>
                  );
                })}
              </div>
            )}
          </section>
        </div>
      </div>

      {/* ── CP Details Modal ── */}
      {showCpPopup && (
        <div className="ggd-overlay" onClick={() => setShowCpPopup(false)}>
          <div className="ggd-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ggd-modal-header">
              <div>
                <p className="ggd-eyebrow">CP Growth</p>
                <h2 className="ggd-modal-title">CP Details</h2>
              </div>
              <button className="ggd-modal-close" onClick={() => setShowCpPopup(false)} aria-label="Close">
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M1 1l12 12M13 1L1 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </button>
            </div>

            <div className="ggd-modal-body">
              <div className="ggd-cp-row">
                <span>Base CP</span>
                <strong>{points.baseCp?.toLocaleString()}</strong>
              </div>
              <div className="ggd-cp-row">
                <span>Accumulated CP</span>
                <strong>{points.accumulatedCp?.toLocaleString()}</strong>
              </div>
              <div className="ggd-cp-row">
                <span>Total CP Growth</span>
                <strong>{points.totalCpGrowth?.toLocaleString()}</strong>
              </div>
            </div>

            {/* ── Shared countdown banner — applies to both CP targets ── */}
            {!qualEnded && (
              <div className="ggd-cp-countdown">
                <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
                  <circle cx="7.5" cy="7.5" r="6.5" stroke="currentColor" strokeWidth="1.4"/>
                  <path d="M7.5 4v3.5L10 9.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>
                  <strong>{weeksLeft} {weeksLeft === 1 ? "week" : "weeks"} left</strong> to hit your CP targets · ends 26 Sep 2026
                </span>
              </div>
            )}

            {/* ── 4,000 CP milestone ── */}
            <div className="ggd-cp-progress">
              <div className="ggd-cp-progress-header">
                <span className="ggd-cp-progress-label">
                  {points.totalCpGrowth >= 4000 ? (
                    <span className="ggd-cp-milestone-done">
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <circle cx="7" cy="7" r="6" fill="#059669"/>
                        <path d="M4 7l2 2 4-4" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      4,000 CP — Milestone reached
                    </span>
                  ) : "Progress to 4,000 CP"}
                </span>
                <span className="ggd-cp-progress-count">
                  {Math.max(0, Math.min(points.totalCpGrowth, 4000)).toLocaleString()}
                  <span className="ggd-cp-progress-target"> / 4,000</span>
                </span>
              </div>
              <div className="ggd-cp-bar-track">
                <div
                  className={`ggd-cp-bar-fill ${points.totalCpGrowth >= 4000 ? "ggd-cp-bar-fill--done" : "ggd-cp-bar-fill--blue"}`}
                  style={{ width: `${Math.max(0, Math.min((points.totalCpGrowth / 4000) * 100, 100)).toFixed(1)}%` }}
                />
              </div>
              {points.totalCpGrowth < 4000 ? (
                points.totalCpGrowth < 0 ? (
                  <p className="ggd-cp-progress-hint ggd-cp-progress-hint--negative">
                    Below base CP — earn {Math.round(points.weeklyAvg4000)?.toLocaleString()} CP/week to recover &amp; reach +20 pts
                  </p>
                ) : (
                  <p className="ggd-cp-progress-hint">
                    Earn {Math.round(points.weeklyAvg4000)?.toLocaleString()} CP/week · rewards +20 pts
                  </p>
                )
              ) : (
                <p className="ggd-cp-progress-hint ggd-cp-progress-hint--earned">
                  +20 pts earned
                </p>
              )}
            </div>

            {/* ── 8,000 CP milestone ── */}
            <div className="ggd-cp-progress">
              <div className="ggd-cp-progress-header">
                <span className="ggd-cp-progress-label">
                  {points.totalCpGrowth >= 8000 ? (
                    <span className="ggd-cp-milestone-done">
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <circle cx="7" cy="7" r="6" fill="#059669"/>
                        <path d="M4 7l2 2 4-4" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      8,000 CP — Milestone reached
                    </span>
                  ) : "Progress to 8,000 CP"}
                </span>
                <span className="ggd-cp-progress-count">
                  {Math.max(0, Math.min(points.totalCpGrowth, 8000)).toLocaleString()}
                  <span className="ggd-cp-progress-target"> / 8,000</span>
                </span>
              </div>
              <div className="ggd-cp-bar-track">
                <div
                  className={`ggd-cp-bar-fill ${points.totalCpGrowth >= 8000 ? "ggd-cp-bar-fill--done" : "ggd-cp-bar-fill--violet"}`}
                  style={{ width: `${Math.max(0, Math.min((points.totalCpGrowth / 8000) * 100, 100)).toFixed(1)}%` }}
                />
              </div>
              {points.totalCpGrowth < 8000 ? (
                points.totalCpGrowth < 0 ? (
                  <p className="ggd-cp-progress-hint ggd-cp-progress-hint--negative">
                    Below base CP — earn {Math.round(points.weeklyAvg8000)?.toLocaleString()} CP/week to recover &amp; reach another +20 pts (total 40 pts)
                  </p>
                ) : (
                  <p className="ggd-cp-progress-hint">
                    Earn {Math.round(points.weeklyAvg8000)?.toLocaleString()} CP/week · rewards another +20 pts (total 40 pts)
                  </p>
                )
              ) : (
                <p className="ggd-cp-progress-hint ggd-cp-progress-hint--earned">
                  +20 pts earned · 40 pts total from CP growth
                </p>
              )}
            </div>

            <button className="ggd-btn-primary" onClick={() => setShowCpPopup(false)}>
              Done
            </button>
          </div>
        </div>
      )}

      {/* ── Coming Soon Modal ── */}
      {showComingSoon && (
        <div className="ggd-overlay" onClick={() => setShowComingSoon(false)}>
          <div className="ggd-modal ggd-coming-soon-modal" onClick={(e) => e.stopPropagation()}>
            <button
              className="ggd-modal-close"
              onClick={() => setShowComingSoon(false)}
              aria-label="Close"
              style={{ marginLeft: "auto", display: "flex" }}
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M1 1l12 12M13 1L1 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
            <div className="ggd-cs-icon">
              <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                <circle cx="16" cy="16" r="14" stroke="currentColor" strokeWidth="1.8" />
                <path d="M16 10v7M16 20v1.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </div>
            <div className="ggd-cs-body">
              <p className="ggd-eyebrow">Coming Soon</p>
              <h2 className="ggd-cs-title">Stay Tuned!</h2>
              <p className="ggd-cs-desc">
                <strong>{comingSoonFeature}</strong> is currently in development.
                We're working hard to bring you this feature — check back soon!
              </p>
            </div>
            <button className="ggd-btn-primary" onClick={() => setShowComingSoon(false)}>
              Got it
            </button>
          </div>
        </div>
      )}
    </div>
  );
}